// Catalog.ID Client — Account-Blob RPC Handlers
// ============================================================================
// The vault side of the four blob modes (cid-encrypted-storage §4.3). A module
// names a blob; we prefix it with the module's HUB-ATTESTED identity and make
// the HTTP call under the member's session. The prefix is OURS TO APPLY — a
// caller cannot opt out of it, cannot choose it, and cannot see another
// namespace, because §4.2 makes the namespace the entire authorisation model
// of the store.
//
// Posture matches `derive`: `requiresUserInteraction:false` in the steady
// state — pushing a vault or pulling a wallet list happens inside a flow the
// user started somewhere else, and nothing here shows a dialog that would need
// the tab raised BEFORE delivery. But a MISSING SESSION is no longer a
// fail-fast (M2c): the handler raises the tab itself and waits for the login,
// because a module that cannot reach the vault is stuck either way.
//
// NO CONSENT DIALOG (§4.4): the bytes are ciphertext the vault cannot read,
// under a key the module derived without a dialog, staying inside the module's
// own namespace. Consent belongs where PLAINTEXT crosses a module boundary.
//
// Gate stack (post-relay signature verification), modelled gate-for-gate on
// `derive-handlers.ts`:
//   1. Hub-stamped `_senderServiceId` equals the claimed `requesterId`.
//   2. Mode is one of the four.
//   3. An identity is active.
//   4. `requesterId` is a legal module id AND not reserved (`cid`, `shared`) —
//      a module may never speak for the vault's own namespace (§4.1).
//   5. Required fields per mode, and the name is in the server's vocabulary.
//   6. Put bodies decode and fit the caps.
//   7. Put digest binding — the canonical envelope commits only to the digest,
//      so without this a relay-verified envelope could be paired with a
//      substituted name, generation, nonce or ciphertext.
//   8. The member has a session (wait-for-unlock only when interactive).
//   9. Call, with `blobKey = requesterId + "/" + name`, and map the refusals.
//
// Refusal vocabulary: `sender-mismatch`, `unsupported-mode`,
// `no-active-identity`, `module-id-not-permitted`, `reserved-namespace`,
// `missing-required-field`, `bad-name`, `bad-generation`, `bad-base64`,
// `bad-nonce`, `blob-too-large`, `body-digest-mismatch`, `cid-locked`,
// `not-found`, `generation-mismatch` (with `generation`), `quota-exceeded`,
// `server-error:<status>`.

import { sha512 } from '@noble/hashes/sha2.js';
import * as aes from '../crypto/aes';
import { ApiError } from '../api/client';
import { BlobConflictError, BlobNotFoundError, BlobQuotaError } from '../api/blobs';
import { isValidModuleId, isReservedModuleId } from '../crypto/derive';
import { isValidBlobName, MAX_BLOB_BASE64_CHARS } from '../crypto/blob';
import type {
    BlobRequest, BlobResponse, BlobRequestMode, BlobDispatchDeps, BlobManifestItem,
} from './blob-types';
import { ensureUnlocked } from './unlock-gate';

type ResponseBase = Pick<BlobResponse, 'type' | 'to' | 'requestId'>;

/**
 * Longest nonce the server's `varchar(64)` column takes. A longer one is a
 * client bug; refusing it here turns a 400 round trip into an immediate answer.
 */
const MAX_NONCE_BASE64_CHARS = 64;

const BLOB_MODES: readonly BlobRequestMode[] =
    ['blob-list', 'blob-get', 'blob-put', 'blob-delete'];

/** True when `mode` belongs to this dispatcher — used by the router in `client.ts`. */
export function isBlobRequestMode(mode: string | undefined): mode is BlobRequestMode {
    return mode !== undefined && (BLOB_MODES as readonly string[]).includes(mode);
}

/** Separator between the digest's fields. A legal name, a decimal integer and
 *  base64 all exclude it, so the split points are unambiguous. */
const FIELD_SEPARATOR = 0x00;

/**
 * The digest a `blob-put`'s canonical envelope commits to:
 *
 *   base64( SHA-512( utf8(name) ‖ 0x00 ‖ utf8(decimal ifGeneration)
 *                    ‖ 0x00 ‖ utf8(nonceBase64) ‖ 0x00 ‖ utf8(ciphertextBase64) ) )
 *
 * All four fields are in it because each changes what the write DOES: the name
 * chooses the document, the generation turns a compare-and-set into a clobber,
 * and the nonce is as load-bearing as the ciphertext for AES-GCM.
 *
 * The C# twin is `BlobModeCanonicalTests.PutBodyDigest` (and the requesting
 * module's own copy); both sides pin the SAME golden vector, because a drift
 * refuses every put with `body-digest-mismatch` and looks exactly like an
 * attack.
 */
export function blobPutBodyDigest(
    name: string,
    ifGeneration: number,
    nonceBase64: string,
    ciphertextBase64: string,
): string {
    const parts = [name, String(ifGeneration), nonceBase64, ciphertextBase64]
        .map(p => aes.encodeUtf8(p));
    const total = parts.reduce((n, p) => n + p.length, 0) + (parts.length - 1);
    const buf = new Uint8Array(total);
    let at = 0;
    parts.forEach((p, i) => {
        if (i > 0) buf[at++] = FIELD_SEPARATOR;
        buf.set(p, at);
        at += p.length;
    });
    return aes.toBase64(sha512(buf));
}

export async function dispatchBlobRequest(
    request: BlobRequest,
    deps: BlobDispatchDeps,
): Promise<BlobResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };
    const refuse = (reason: string, extra?: Partial<BlobResponse>): BlobResponse =>
        ({ ...base, status: 'unavailable', reason, ...extra });

    // Gate 1: trusted-sender binding. Defence in depth behind the hub's own
    // enforcement — a registered-but-misbehaving module must not be able to
    // claim another module's NAMESPACE by lying in `requesterId`.
    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return refuse('sender-mismatch');
    }

    // Gate 2.
    if (!isBlobRequestMode(request.mode)) return refuse('unsupported-mode');

    // Gate 3.
    if (!deps.getActiveCid()) return refuse('no-active-identity');

    // Gate 4: the module id becomes the blob prefix, so it is validated before
    // use rather than trusted because the relay vouched for the envelope.
    const moduleId = request.requesterId;
    if (!isValidModuleId(moduleId)) return refuse('module-id-not-permitted');
    // `cid` is the vault's own namespace and `shared` is held back (§4.1). The
    // module registry refuses both at registration; this is the second line, so
    // a stale registration cannot reach the vault's own documents.
    if (isReservedModuleId(moduleId)) return refuse('reserved-namespace');

    // Gate 5: fields. `blob-list` needs nothing beyond the envelope.
    let name = '';
    if (request.mode !== 'blob-list') {
        if (request.name === undefined || request.name === '') {
            return refuse('missing-required-field');
        }
        name = request.name;
        if (!isValidBlobName(name)) return refuse('bad-name');
    }

    let ciphertextBase64 = '';
    let nonceBase64 = '';
    let ifGeneration = 0;
    if (request.mode === 'blob-put') {
        if (request.ciphertextBase64 === undefined || request.nonceBase64 === undefined
            || request.ifGeneration === undefined || request.bodyDigestBase64 === undefined) {
            return refuse('missing-required-field');
        }
        ciphertextBase64 = request.ciphertextBase64;
        nonceBase64 = request.nonceBase64;
        ifGeneration = request.ifGeneration;
        if (!Number.isInteger(ifGeneration) || ifGeneration < 0) return refuse('bad-generation');

        // Gate 6: the body. Decoding is checked here so a malformed blob is
        // refused by the vault rather than stored as bytes nobody can open.
        try {
            aes.fromBase64(ciphertextBase64);
            aes.fromBase64(nonceBase64);
        } catch {
            return refuse('bad-base64');
        }
        if (ciphertextBase64.length === 0) return refuse('bad-base64');
        if (ciphertextBase64.length > MAX_BLOB_BASE64_CHARS) return refuse('blob-too-large');
        if (nonceBase64.length === 0 || nonceBase64.length > MAX_NONCE_BASE64_CHARS) {
            return refuse('bad-nonce');
        }

        // Gate 7: digest binding.
        const expected = blobPutBodyDigest(name, ifGeneration, nonceBase64, ciphertextBase64);
        if (expected !== request.bodyDigestBase64) return refuse('body-digest-mismatch');
    }

    // Gate 8: the member's session. Blob modes spend the SESSION, not a key —
    // the vault never opens these documents. The bag's load event is the proxy
    // for "the user finished logging in": a login installs the session token
    // and loads the bag together, and it is the signal `derive` already waits
    // on. M2c: no session ⇒ raise this tab and wait, whatever the sender's
    // flag says; `cid-locked` only once the budget is spent.
    const sessionReady = await ensureUnlocked(request, {
        isUnlocked: deps.hasSession,
        requestFocus: deps.requestFocus,
        unlockWaitTimeoutMs: deps.unlockWaitTimeoutMs,
    });
    if (!sessionReady) return refuse('cid-locked');

    // Gate 9: serve.
    try {
        switch (request.mode) {
            case 'blob-list': {
                const { blobs } = await deps.api.listBlobs();
                // The server manifest is ACCOUNT-WIDE. Filtering and stripping
                // here is what keeps one module from learning which other
                // modules the member uses, let alone their document names.
                const prefix = moduleId + '/';
                const mine: BlobManifestItem[] = blobs
                    .filter(b => b.blobKey.startsWith(prefix))
                    .map(b => ({
                        name: b.blobKey.slice(prefix.length),
                        generation: b.generation,
                        size: b.size,
                        lastModifiedAt: b.lastModifiedAt,
                    }));
                return { ...base, status: 'approved', blobs: mine };
            }
            case 'blob-get': {
                const row = await deps.api.getBlob(moduleId, name);
                return {
                    ...base,
                    status: 'approved',
                    name,
                    generation: row.generation,
                    ciphertextBase64: row.ciphertext,
                    nonceBase64: row.nonce,
                    lastModifiedAt: row.lastModifiedAt,
                };
            }
            case 'blob-put': {
                const result = await deps.api.putBlob(
                    moduleId, name, { ciphertext: ciphertextBase64, nonce: nonceBase64 },
                    ifGeneration);
                return { ...base, status: 'approved', generation: result.generation };
            }
            case 'blob-delete': {
                await deps.api.deleteBlob(moduleId, name);
                return { ...base, status: 'approved' };
            }
        }
    } catch (err) {
        return refuse(...failureFor(err));
    }
}

/**
 * Map a failed call onto the caller's refusal vocabulary. A conflict is not an
 * error to the module — it is the compare-and-set protocol working — so it
 * carries the generation the caller must re-read against.
 */
function failureFor(err: unknown): [string, Partial<BlobResponse>?] {
    if (err instanceof BlobConflictError) {
        return ['generation-mismatch', { generation: err.generation }];
    }
    if (err instanceof BlobNotFoundError) return ['not-found'];
    if (err instanceof BlobQuotaError) return ['quota-exceeded'];
    if (err instanceof ApiError) return [`server-error:${err.status}`];
    // A transport failure (the CID tab lost the server) is not a protocol
    // state; the module retries. Status 0 says "never reached the server".
    return ['server-error:0'];
}
